import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, MapPin, AlertTriangle, RotateCw, Loader2, Sprout } from "lucide-react";

const SEASONS = [
  { label: "Warm-season (50°F)", value: "warm" },
  { label: "Cool-season (32°F)", value: "cool" },
];

const BANNER = {
  active: { bg: "bg-[var(--ld-lime)]/15", border: "border-[var(--ld-lime)]/40", text: "text-[var(--ld-lime-bright)]", icon: "text-[var(--ld-lime)]" },
  before: { bg: "bg-[var(--ld-amber)]/12", border: "border-[var(--ld-amber)]/40", text: "text-[var(--ld-amber)]", icon: "text-[var(--ld-amber)]" },
  late: { bg: "bg-[var(--ld-surface-2)]", border: "border-[var(--ld-line)]", text: "text-[var(--ld-muted)]", icon: "text-[var(--ld-muted)]" },
};

const startLabel = (iso) => {
  if (!iso) return "start";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Per-day GDD bars, coloured history vs forecast.
function DailyBars({ series, metric }) {
  if (!series || series.length === 0) return null;
  const w = 520, h = 120, gap = 3;
  const n = series.length;
  const bw = (w - gap * (n - 1)) / n;
  const max = Math.max(...series.map((d) => d.daily_gdd), 1);
  const k = metric ? 5 / 9 : 1;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-32 w-full" preserveAspectRatio="none">
      {series.map((d, i) => {
        const bh = Math.max(2, (d.daily_gdd / max) * (h - 6));
        const x = i * (bw + gap);
        const isFc = d.source === "forecast";
        return (
          <motion.rect
            key={d.date}
            x={x}
            y={h - bh}
            width={bw}
            height={bh}
            rx={Math.min(2, bw / 2)}
            fill={isFc ? "var(--ld-amber)" : "var(--ld-lime-bright)"}
            opacity={isFc ? 0.75 : 1}
            initial={{ height: 0, y: h }}
            animate={{ height: bh, y: h - bh }}
            transition={{ duration: 0.5, delay: i * 0.02 }}
          >
            <title>{`${d.date}: ${(d.daily_gdd * k).toFixed(1)} GDD${isFc ? " (forecast)" : ""}`}</title>
          </motion.rect>
        );
      })}
    </svg>
  );
}

const Stat = ({ label, value, unit }) => (
  <div className="rounded-xl bg-[var(--ld-bg)] px-3 py-2.5">
    <div className="ld-display text-xl text-[var(--ld-text)]">
      {value}
      {unit && <span className="ml-0.5 text-xs font-black text-[var(--ld-muted)]">{unit}</span>}
    </div>
    <div className="mt-0.5 text-[10px] font-black uppercase tracking-wider text-[var(--ld-muted)]">{label}</div>
  </div>
);

export default function GddTool({ idPrefix = "gdd", data = null, loading = false, error = "", gs = "warm", onSeasonChange, onRetry }) {
  const [metric, setMetric] = useState(false);

  const k = metric ? 5 / 9 : 1;
  const gddU = metric ? "°C·d" : "GDD";
  const toC = (f) => (f == null ? null : (f - 32) * (5 / 9));
  const tempVal = (f) => (f == null ? "—" : `${(metric ? toC(f) : f).toFixed(1)}°`);
  const gddVal = (g) => (g == null ? "—" : Math.round(g * k).toLocaleString());

  const pe = data?.pre_emergent;
  const bc = pe ? BANNER[pe.level] : null;

  return (
    <div
      data-testid={`${idPrefix}-tool`}
      className="rounded-3xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/80 p-6 backdrop-blur-xl sm:p-7"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">
          <TrendingUp className="h-3.5 w-3.5" /> Growing Degree Days
        </div>
        <button
          onClick={() => setMetric((m) => !m)}
          data-testid={`${idPrefix}-unit-toggle`}
          className="rounded-full border border-[var(--ld-line)] px-3 py-1.5 text-[11px] font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50"
        >
          {metric ? "°C" : "°F"}
        </button>
      </div>

      {/* Hero number + phase */}
      <div className="flex items-end gap-2">
        <span data-testid={`${idPrefix}-value`} className="ld-display text-6xl sm:text-7xl text-[var(--ld-lime-bright)]">
          {gddVal(data?.gdd_to_date)}
        </span>
        <span className="mb-2 text-sm font-bold text-[var(--ld-muted)]">{gddU}</span>
        {data && (
          <span className="mb-2 ml-auto flex items-center gap-1 text-right text-xs font-bold text-[var(--ld-muted)]">
            <MapPin className="h-3.5 w-3.5 shrink-0" /> {data.location.city}{data.location.state ? `, ${data.location.state}` : ""}
          </span>
        )}
      </div>

      {data ? (
        <p className="mt-1 text-sm font-semibold text-[var(--ld-text)]" data-testid={`${idPrefix}-phase`}>
          <span className="text-[var(--ld-lime)]">{data.phase.label}</span>
          <span className="text-[var(--ld-muted)]"> · since {startLabel(data.season_start)} · base {metric ? `${((data.base_temp_f - 32) * 5 / 9).toFixed(0)}°C` : `${data.base_temp_f}°F`}</span>
          <br />
          <span className="text-[var(--ld-muted)]">{data.phase.sub}</span>
        </p>
      ) : (
        <p className="mt-1 text-sm font-semibold text-[var(--ld-muted)]">
          {loading ? "Crunching your season…" : "Search a location above to track heat accumulation, phase and pre-emergent timing."}
        </p>
      )}

      {/* Pre-emergent banner */}
      {pe?.show_banner && (
        <div className={`mt-4 flex items-start gap-2.5 rounded-xl border px-4 py-3 ${bc.bg} ${bc.border}`} data-testid={`${idPrefix}-preemergent`}>
          <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${bc.icon}`} />
          <div>
            <div className={`text-sm font-black ${bc.text}`}>{pe.label}</div>
            <div className="text-xs font-semibold text-[var(--ld-muted)]">{pe.explainer}</div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="mt-4 rounded-2xl bg-[var(--ld-bg)] p-3">
        {data ? (
          <>
            <DailyBars series={data.daily_series} metric={metric} />
            <div className="mt-2 flex items-center justify-center gap-4 text-[10px] font-bold uppercase tracking-wider text-[var(--ld-muted)]">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[var(--ld-lime-bright)]" /> Actual</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[var(--ld-amber)]" /> 7-day forecast</span>
            </div>
          </>
        ) : error ? (
          <div className="flex h-32 flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm font-bold text-red-400" data-testid={`${idPrefix}-error`}>{error}</p>
            <button
              onClick={onRetry}
              data-testid={`${idPrefix}-retry`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ld-line)] px-4 py-2 text-xs font-black text-[var(--ld-text)] transition-colors hover:border-[var(--ld-lime)]/50"
            >
              <RotateCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center text-sm font-bold text-[var(--ld-muted)]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin text-[var(--ld-lime)]" /> : "Search a location above to plot your season"}
          </div>
        )}
      </div>

      {/* Stat cards */}
      {data && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={`${idPrefix}-stats`}>
          <Stat label={`Since ${startLabel(data.season_start)}`} value={gddVal(data.gdd_to_date)} unit={gddU} />
          <Stat label="Projected 7d" value={gddVal(data.gdd_projected_7d)} unit={gddU} />
          <Stat label="Avg daily" value={data.avg_daily_gdd != null ? (data.avg_daily_gdd * k).toFixed(1) : "—"} unit={gddU} />
          <Stat label="Soil temp" value={tempVal(data.soil_temp_now_f)} />
        </div>
      )}

      {/* Season toggle */}
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {SEASONS.map((s) => (
          <button
            key={s.value}
            onClick={() => onSeasonChange?.(s.value)}
            disabled={loading}
            data-testid={`${idPrefix}-season-${s.value}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black transition-colors disabled:opacity-60 ${
              gs === s.value
                ? "bg-[var(--ld-lime)] text-[var(--ld-bg)]"
                : "bg-[var(--ld-surface-2)] text-[var(--ld-muted)] hover:text-[var(--ld-text)]"
            }`}
          >
            <Sprout className="h-3.5 w-3.5" /> {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
